/**
 * Terrain system — evaluates trig-function-based terrain for ground/ceiling.
 *
 * Given a terrain definition (from level data), computes the ground Y
 * at any world X position, and renders the terrain surface.
 */

import type { TerrainDef, TerrainFunctionDef, LevelSegment } from './level';
import type { Renderer } from '@/rendering/renderer';
import type { Color } from '@/types';

/** Evaluate a terrain function at a given x position */
export function evaluateTerrainFunction(def: TerrainFunctionDef, x: number): number {
  const amp = def.amplitude ?? 0;
  const freq = def.frequency ?? 0.01;
  const phase = def.phase ?? 0;

  switch (def.function) {
    case 'sin':
      return def.offset + amp * Math.sin(freq * x + phase);
    case 'cos':
      return def.offset + amp * Math.cos(freq * x + phase);
    case 'tan': {
      // Clamp tan to avoid infinity
      const raw = Math.tan(freq * x + phase);
      const clamped = Math.max(-3, Math.min(3, raw));
      return def.offset + amp * clamped;
    }
    case 'constant':
      return def.offset;
    default:
      return def.offset;
  }
}

/** Analytic derivative (slope) of a terrain function at x */
export function terrainFunctionDerivative(def: TerrainFunctionDef, x: number): number {
  const amp = def.amplitude ?? 0;
  const freq = def.frequency ?? 0.01;
  const phase = def.phase ?? 0;

  switch (def.function) {
    case 'sin':
      return amp * freq * Math.cos(freq * x + phase);
    case 'cos':
      return -amp * freq * Math.sin(freq * x + phase);
    case 'tan': {
      const c = Math.cos(freq * x + phase);
      // Clamp to avoid infinite slope near discontinuities
      const safe = Math.max(0.1, Math.abs(c));
      return (amp * freq) / (safe * safe);
    }
    default:
      return 0;
  }
}

/** Get the ground tangent angle (radians) at a world-x position */
export function getGroundTangentAngle(
  segments: LevelSegment[],
  segmentOffsets: number[],
  worldX: number,
): number {
  for (let i = 0; i < segments.length; i++) {
    const segStart = segmentOffsets[i];
    const segEnd = segStart + segments[i].length;
    if (worldX >= segStart && worldX < segEnd) {
      const terrain = segments[i].terrain;
      if (terrain.floor) {
        const localX = worldX - segStart;
        const slope = terrainFunctionDerivative(terrain.floor, localX);
        return Math.atan(slope);
      }
      return 0;
    }
  }
  return 0;
}

/** Get the ground Y at a world-x position, considering segment terrain */
export function getGroundY(
  segments: LevelSegment[],
  segmentOffsets: number[],
  worldX: number,
  defaultGroundY: number,
): number {
  // Find which segment this x falls into
  for (let i = 0; i < segments.length; i++) {
    const segStart = segmentOffsets[i];
    const segEnd = segStart + segments[i].length;
    if (worldX >= segStart && worldX < segEnd) {
      const terrain = segments[i].terrain;
      if (terrain.floor) {
        const localX = worldX - segStart;
        return evaluateTerrainFunction(terrain.floor, localX);
      }
      return defaultGroundY;
    }
  }
  return defaultGroundY;
}

/** Get the ceiling Y at a world-x position (null = no ceiling) */
export function getCeilingY(
  segments: LevelSegment[],
  segmentOffsets: number[],
  worldX: number,
): number | null {
  for (let i = 0; i < segments.length; i++) {
    const segStart = segmentOffsets[i];
    const segEnd = segStart + segments[i].length;
    if (worldX >= segStart && worldX < segEnd) {
      const terrain = segments[i].terrain;
      if (terrain.ceiling) {
        const localX = worldX - segStart;
        return evaluateTerrainFunction(terrain.ceiling, localX);
      }
      return null;
    }
  }
  return null;
}

/** Render terrain surface as a series of line segments + filled area below */
export function renderTerrain(
  renderer: Renderer,
  segments: LevelSegment[],
  segmentOffsets: number[],
  scrollOffset: number,
  respawnX: number,
  viewLeft: number,
  viewRight: number,
  defaultGroundY: number,
  color: Color,
): void {
  const step = 4; // pixels between terrain sample points
  const fillColor: Color = { r: color.r * 0.15, g: color.g * 0.15, b: color.b * 0.15, a: 0.6 };
  const lineColor: Color = { ...color, a: 0.9 };

  // We need to render terrain in screen space
  // Screen X = worldX - scrollOffset + respawnX
  // So worldX = screenX + scrollOffset - respawnX
  const worldLeft = viewLeft + scrollOffset - respawnX;
  const worldRight = viewRight + scrollOffset - respawnX;

  let prevScreenX = viewLeft;
  let prevGroundY = getGroundY(segments, segmentOffsets, worldLeft, defaultGroundY);
  const bottomY = defaultGroundY - 200; // well below screen

  for (let worldX = worldLeft + step; worldX <= worldRight + step; worldX += step) {
    const groundY = getGroundY(segments, segmentOffsets, worldX, defaultGroundY);
    const screenX = worldX - scrollOffset + respawnX;
    const prevSX = prevScreenX;

    // Draw filled quad below terrain surface
    renderer.drawTriangle(
      prevSX, prevGroundY,
      screenX, groundY,
      prevSX, bottomY,
      fillColor,
    );
    renderer.drawTriangle(
      screenX, groundY,
      screenX, bottomY,
      prevSX, bottomY,
      fillColor,
    );

    // Draw terrain surface line
    renderer.drawLine(prevSX, prevGroundY, screenX, groundY, 2, lineColor);

    prevScreenX = screenX;
    prevGroundY = groundY;
  }

  // Also render ceiling if present
  for (let worldX = worldLeft; worldX <= worldRight + step; worldX += step) {
    const ceilingY = getCeilingY(segments, segmentOffsets, worldX);
    if (ceilingY === null) continue;

    const nextWorldX = Math.min(worldX + step, worldRight + step);
    const nextCeilingY = getCeilingY(segments, segmentOffsets, nextWorldX);
    if (nextCeilingY === null) continue;

    const sx1 = worldX - scrollOffset + respawnX;
    const sx2 = nextWorldX - scrollOffset + respawnX;
    const topY = ceilingY + 200; // well above screen

    renderer.drawTriangle(sx1, ceilingY, sx2, nextCeilingY, sx1, topY, fillColor);
    renderer.drawTriangle(sx2, nextCeilingY, sx2, topY, sx1, topY, fillColor);
    renderer.drawLine(sx1, ceilingY, sx2, nextCeilingY, 2, lineColor);
  }
}
